/**
 * GDPR consent state.
 *
 * Nothing outside the "necessary" category may read or write storage until the
 * user has actively opted in. Consent is versioned so that a material change to
 * what we collect re-prompts everyone rather than silently inheriting old consent.
 */
export const CONSENT_KEY = 'ssipl.consent';
export const CONSENT_VERSION = 1;
export const CONSENT_CHANGE_EVENT = 'ssipl:consent-change';
export const CONSENT_OPEN_EVENT = 'ssipl:consent-open';

export const CATEGORIES = [
  {
    id: 'necessary',
    label: 'Strictly necessary',
    required: true,
    description:
      'Required for the site to function, such as remembering your cookie choice and keeping the site secure. These cannot be switched off.'
  },
  {
    id: 'analytics',
    label: 'Analytics',
    required: false,
    description:
      'Help us understand which pages are useful so we can improve the site. Aggregated and never used to identify you personally.'
  },
  {
    id: 'marketing',
    label: 'Marketing',
    required: false,
    description:
      'Used to measure the effectiveness of our campaigns and to show you relevant SSIPL content on other platforms.'
  }
];

export const DEFAULT_PREFERENCES = { necessary: true, analytics: false, marketing: false };

export const readConsent = () => {
  try {
    const raw = window.localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Treat consent recorded against an older policy version as absent.
    if (!parsed || parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    // Private mode or storage disabled: behave as if no consent was given.
    return null;
  }
};

export const writeConsent = (preferences) => {
  const record = {
    version: CONSENT_VERSION,
    decidedAt: new Date().toISOString(),
    preferences: { ...DEFAULT_PREFERENCES, ...preferences, necessary: true }
  };
  try {
    window.localStorage.setItem(CONSENT_KEY, JSON.stringify(record));
  } catch {
    // Non-fatal: the choice still applies for this page view.
  }
  window.dispatchEvent(new CustomEvent(CONSENT_CHANGE_EVENT, { detail: record }));
  return record;
};

export const revokeConsent = () => {
  try {
    window.localStorage.removeItem(CONSENT_KEY);
  } catch {
    // Ignore.
  }
  window.dispatchEvent(new CustomEvent(CONSENT_CHANGE_EVENT, { detail: null }));
};

/** Lets any part of the site (for example the footer link) reopen the preferences dialog. */
export const openConsentPreferences = () => window.dispatchEvent(new CustomEvent(CONSENT_OPEN_EVENT));

export const hasConsent = (category) => {
  const record = readConsent();
  return Boolean(record?.preferences?.[category]);
};
